import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useGameStore from '../store/gameStore';
import { SENDER_TYPE_LABELS } from '../data/messageTemplates';
import { formatMoney } from '../utils/format';
import { isUrgentMessage, getPendingUrgentMessages } from '../utils/messagePriority';
import ClientTierBadge from '../components/game/ClientTierBadge';
import ClientLogo from '../components/crm/ClientLogo';
import MarkdownLite from '../components/MarkdownLite';
import { OFFICE_UPGRADE_BY_ID } from '../data/officeUpgrades';
import {
  Mail, MailOpen, Inbox, Truck, Building2, Banknote, Sword, Scale,
  Newspaper, ClipboardCheck, Briefcase, Bell, AlertCircle, Clock,
  Check, ChevronRight, Filter, X, Sparkles, GraduationCap, Heart, Siren,
} from 'lucide-react';

const SENDER_ICON = {
  driver:     Truck,
  client:     Building2,
  supplier:   Briefcase,
  bank:       Banknote,
  competitor: Sword,
  urssaf:     Scale,
  // 17/06/2026 (Lot 6 Phase L2) — Procédures juridiques structurées.
  legal:      Scale,
  media:      Newspaper,
  inspector:  ClipboardCheck,
  hr:         Bell,
  support:    Briefcase,
  system:     Sparkles,
  news:       Newspaper,
  supervisor: GraduationCap,
};

// 05/02/2026 — Q1 retour user prod : lisibilité light theme améliorée
// (anciennes couleurs text-X-300 sur bg-X-500/15 illisibles en clair).
// Pattern dual-mode `dark:text-X-300 text-X-700`.
const TONE_BADGE = {
  friendly:   { label: 'Cordial',   className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40 dark:border-emerald-500/30' },
  formal:     { label: 'Formel',    className: 'bg-slate-500/15   text-slate-700  dark:text-slate-300  border-slate-500/40 dark:border-slate-500/30'   },
  aggressive: { label: 'Sec',       className: 'bg-rose-500/15    text-rose-700   dark:text-rose-300   border-rose-500/40 dark:border-rose-500/30'    },
  humour:     { label: 'Humour',    className: 'bg-violet-500/15  text-violet-700 dark:text-violet-300 border-violet-500/40 dark:border-violet-500/30' },
  urgent:     { label: 'Urgent',    className: 'bg-orange-500/15  text-orange-700 dark:text-orange-300 border-orange-500/40 dark:border-orange-500/30' },
  neutral:    { label: 'Neutre',    className: 'bg-ink-700        text-slate-600  dark:text-slate-400  border-ink-600'                                  },
};

const Messagerie = () => {
  const { inbox, totalDays, markMessageRead, applyMessageChoice } = useGameStore();
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'decision' | 'urgent' | senderType
  const [selectedId, setSelectedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // 18/09/2026 (REX apprenant) — degrés d'importance : un message urgent
  // (`priority: 'urgent'`, ton urgent ou décision qui expire sous 3 j) est
  // épinglé en tête, badgé URGENT et ouvert directement à l'arrivée sur la
  // page (ou via `?open=<id>` depuis le bandeau du tableau de bord).
  const openParam = searchParams.get('open');
  useEffect(() => {
    const list = inbox || [];
    if (openParam) {
      const target = list.find(m => m.id === openParam);
      if (target) {
        setSelectedId(target.id);
        if (!target.read) markMessageRead(target.id);
      }
      setSearchParams({}, { replace: true });
      return;
    }
    if (selectedId) return;
    const urgent = getPendingUrgentMessages(list, totalDays, 1)[0];
    if (urgent) {
      setSelectedId(urgent.id);
      markMessageRead(urgent.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam]);
  // 08/02/2026 — Q2 user prod : toggle pour masquer les messages purement
  // narratifs (ambiance/random roll). Persisté en localStorage. Les
  // messages narratifs sont marqués `isNarrative: true` par messageSlice
  // au moment du random roll quotidien.
  const [hideNarrative, setHideNarrative] = useState(() => {
    try { return localStorage.getItem('hst-hide-narrative') === '1'; } catch { return false; }
  });
  const toggleHideNarrative = () => {
    setHideNarrative(v => {
      const next = !v;
      try { localStorage.setItem('hst-hide-narrative', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const filtered = useMemo(() => {
    let list = [...(inbox || [])];
    if (hideNarrative) list = list.filter(m => !m.isNarrative);
    if (filter === 'unread')   list = list.filter(m => !m.read);
    else if (filter === 'decision') list = list.filter(m => m.kind === 'decision' && !m.replied);
    else if (filter === 'urgent') list = list.filter(m => isUrgentMessage(m, totalDays));
    else if (filter !== 'all') list = list.filter(m => m.senderType === filter);
    // Urgents non lus épinglés en tête, puis ordre chronologique inverse.
    const pin = (m) => (!m.read && isUrgentMessage(m, totalDays) ? 1 : 0);
    return list.sort((a, b) => (pin(b) - pin(a)) || (b.receivedDay - a.receivedDay));
  }, [inbox, filter, hideNarrative, totalDays]);

  const selected = (inbox || []).find(m => m.id === selectedId) || null;
  const unreadCount = (inbox || []).filter(m => !m.read).length;
  const narrativeCount = (inbox || []).filter(m => m.isNarrative).length;
  const urgentCount = (inbox || []).filter(m => !m.read && isUrgentMessage(m, totalDays)).length;

  // Stats by sender type for sidebar.
  const senderCounts = useMemo(() => {
    const counts = {};
    (inbox || []).forEach(m => {
      counts[m.senderType] = (counts[m.senderType] || 0) + (m.read ? 0 : 1);
    });
    return counts;
  }, [inbox]);

  const openMessage = (msg) => {
    setSelectedId(msg.id);
    if (!msg.read) markMessageRead(msg.id);
  };

  const handleChoice = (msgId, choiceId) => {
    const result = applyMessageChoice(msgId, choiceId);
    if (result && !result.ok && result.reason === 'fleet_saturated') {
      const detail = result.missing === 'vehicle'
        ? 'Aucun camion disponible (tous les camions sont en mission ou indisponibles).'
        : 'Aucun chauffeur disponible (tous sont en mission, congés, formation, etc.).';
      window.alert(
        `Opportunité refusée — flotte saturée.\n\n${detail}\n\n` +
        `Libérez une paire camion + chauffeur, puis utilisez « Étudier » pour différer la décision.`,
      );
    }
  };

  return (
    <div className="space-y-3" data-testid="messagerie-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-accent/15">
            <Inbox className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Messagerie</h2>
            <p className="text-xs text-slate-500">{(inbox || []).length} message·s · {unreadCount} non lu·s{narrativeCount > 0 ? ` · ${narrativeCount} narratif·s` : ''}</p>
          </div>
        </div>
        {/* 08/02/2026 — Q2 user prod : toggle « Cacher les messages narratifs ».
            Persisté en localStorage. */}
        <button
          type="button"
          onClick={toggleHideNarrative}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono transition-all ${
            hideNarrative
              ? 'bg-amber-500/15 border-amber-500/50 text-amber-200'
              : 'bg-ink-700/40 border-ink-600 text-slate-400 hover:text-slate-200'
          }`}
          data-testid="toggle-hide-narrative"
          title={hideNarrative ? `${narrativeCount} message·s narratif·s masqué·s` : 'Cacher les messages d\'ambiance / narratifs'}
          aria-pressed={hideNarrative}
        >
          {hideNarrative ? <X className="w-3.5 h-3.5" /> : <Filter className="w-3.5 h-3.5" />}
          {hideNarrative ? 'Narratifs masqués' : 'Cacher narratifs'}
          {narrativeCount > 0 && (
            <span className="ml-1 font-bold">({narrativeCount})</span>
          )}
        </button>
      </div>

      <div className="grid grid-cols-12 gap-3 min-h-[60vh]">
        {/* Sidebar */}
        <aside className="col-span-12 md:col-span-3 bg-ink-800 rounded-lg border border-ink-600 p-2 space-y-1 h-fit">
          <SidebarBtn label="Tous les messages" icon={Inbox} active={filter === 'all'} onClick={() => setFilter('all')} count={(inbox || []).length} testId="filter-all" />
          <SidebarBtn label="Non lus"           icon={Mail}  active={filter === 'unread'} onClick={() => setFilter('unread')} count={unreadCount} testId="filter-unread" highlight />
          <SidebarBtn label="Urgents"           icon={Siren} active={filter === 'urgent'} onClick={() => setFilter('urgent')} count={urgentCount} testId="filter-urgent" highlight danger />
          <SidebarBtn label="Décisions"         icon={AlertCircle} active={filter === 'decision'} onClick={() => setFilter('decision')} count={(inbox || []).filter(m => m.kind === 'decision' && !m.replied).length} testId="filter-decision" highlight />
          <div className="py-1.5"><div className="h-px bg-ink-600" /></div>
          {Object.entries(SENDER_TYPE_LABELS).map(([type, label]) => {
            const Icon = SENDER_ICON[type] || Mail;
            return (
              <SidebarBtn
                key={type}
                label={label}
                icon={Icon}
                active={filter === type}
                onClick={() => setFilter(type)}
                count={senderCounts[type] || 0}
                testId={`filter-${type}`}
              />
            );
          })}
        </aside>

        {/* Message list */}
        <section className={`bg-ink-800 rounded-lg border border-ink-600 overflow-hidden ${selected ? 'col-span-12 md:col-span-4' : 'col-span-12 md:col-span-9'}`}>
          <div className="px-3 py-2 border-b border-ink-600 flex items-center justify-between bg-ink-700/30">
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
              <Filter className="w-3 h-3 text-slate-500" />
              {filter === 'all' ? 'Tous' : filter === 'unread' ? 'Non lus' : filter === 'decision' ? 'Décisions' : filter === 'urgent' ? 'Urgents' : SENDER_TYPE_LABELS[filter]}
              <span className="text-slate-500">· {filtered.length}</span>
            </h3>
          </div>
          <ul className="divide-y divide-ink-600 max-h-[65vh] overflow-y-auto" data-testid="message-list">
            {filtered.length === 0 ? (
              <li className="p-6 text-center text-xs text-slate-500 italic">Aucun message ici.</li>
            ) : (
              filtered.map(m => {
                const Icon = SENDER_ICON[m.senderType] || Mail;
                const isOpen = selectedId === m.id;
                const daysLeft = m.kind === 'decision' && !m.replied ? (m.expiresDay - totalDays) : null;
                const overdue = daysLeft !== null && daysLeft <= 0;
                const urgent = isUrgentMessage(m, totalDays);
                return (
                  <li
                    key={m.id}
                    onClick={() => openMessage(m)}
                    className={`px-3 py-2.5 cursor-pointer hover:bg-ink-700/50 transition-colors ${isOpen ? 'bg-ink-700/70' : ''} ${!m.read ? (urgent ? 'border-l-2 border-l-rose-500 bg-rose-500/5' : 'border-l-2 border-l-accent') : ''}`}
                    data-testid={`message-row-${m.id}`}
                    data-priority={urgent ? 'urgent' : 'normal'}
                  >
                    <div className="flex items-start gap-2">
                      {m.senderType === 'client' && m.senderId ? (
                        <ClientLogo clientId={m.senderId} size="sm" className="flex-shrink-0" />
                      ) : (
                        <div className={`p-1.5 rounded ${m.read ? 'bg-ink-700/40' : 'bg-accent/15'} flex-shrink-0`}>
                          <Icon className={`w-3.5 h-3.5 ${m.read ? 'text-slate-400' : 'text-accent'}`} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-xs truncate ${!m.read ? 'font-bold text-slate-100' : 'font-medium text-slate-300'}`}>
                            {m.senderName}
                          </p>
                          <span className="text-[9px] text-slate-500 flex-shrink-0">J{m.receivedDay}</span>
                        </div>
                        {m.senderType === 'client' && m.senderId && (
                          <ClientTierBadge clientId={m.senderId} size="sm" />
                        )}
                        <p className={`text-xs truncate ${!m.read ? 'text-slate-200' : 'text-slate-400'}`}>{m.subject}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {urgent && (
                            <span className="text-[9px] flex items-center gap-0.5 px-1 rounded font-bold uppercase tracking-wider text-rose-700 dark:text-rose-200 bg-rose-500/20 border border-rose-500/40" data-testid={`urgent-badge-${m.id}`}>
                              <Siren className="w-2.5 h-2.5" />Urgent
                            </span>
                          )}
                          {m.kind === 'decision' && !m.replied && (
                            <span className={`text-[9px] flex items-center gap-0.5 px-1 rounded ${overdue ? 'text-rose-700 dark:text-rose-300 bg-rose-500/15' : 'text-amber-700 dark:text-amber-300 bg-amber-500/15'}`}>
                              <Clock className="w-2.5 h-2.5" />
                              {overdue ? 'Expiré' : `${daysLeft}j restant${daysLeft > 1 ? 's' : ''}`}
                            </span>
                          )}
                          {m.replied && (
                            <span className="text-[9px] flex items-center gap-0.5 px-1 rounded text-emerald-300 bg-emerald-500/15">
                              <Check className="w-2.5 h-2.5" />Traité
                            </span>
                          )}
                          {!m.replied && m.kind === 'info' && (
                            <span className="text-[9px] text-slate-500">{TONE_BADGE[m.tone]?.label || 'Info'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </section>

        {/* Detail panel */}
        {selected && (
          <article className="col-span-12 md:col-span-5 bg-ink-800 rounded-lg border border-ink-600 flex flex-col" data-testid="message-detail">
            <div className="px-4 py-3 border-b border-ink-600 flex items-start justify-between bg-ink-700/30">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {(() => {
                  if (selected.senderType === 'client' && selected.senderId) {
                    return <ClientLogo clientId={selected.senderId} size="lg" className="flex-shrink-0" withBorder />;
                  }
                  const Icon = SENDER_ICON[selected.senderType] || Mail;
                  return (
                    <div className="p-2 rounded-md bg-accent/15 flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" />
                    </div>
                  );
                })()}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-slate-100 truncate">{selected.senderName}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${TONE_BADGE[selected.tone]?.className || ''}`}>
                      {TONE_BADGE[selected.tone]?.label || selected.tone}
                    </span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase tracking-wider ${isUrgentMessage(selected, totalDays) ? 'bg-rose-500/20 text-rose-700 dark:text-rose-200 border-rose-500/40' : 'bg-ink-700 text-slate-500 border-ink-600'}`}
                      data-testid="message-priority"
                    >
                      {isUrgentMessage(selected, totalDays) ? 'Urgent' : 'Normal'}
                    </span>
                    {selected.senderType === 'client' && selected.senderId && (
                      <ClientTierBadge clientId={selected.senderId} size="md" />
                    )}
                  </div>
                  <h3 className="text-base font-bold text-slate-200 mt-0.5" data-testid="message-subject">{selected.subject}</h3>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Reçu J{selected.receivedDay}
                    {selected.kind === 'decision' && !selected.replied && (
                      <> · <span className="text-amber-300">Expire J{selected.expiresDay}</span></>
                    )}
                    {selected.replied && <> · <span className="text-emerald-300">Traité ({selected.resolvedChoiceId})</span></>}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedId(null)}
                className="text-slate-500 hover:text-slate-300 p-1"
                aria-label="Fermer"
                data-testid="close-detail"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-4 py-3 text-sm text-slate-300 leading-relaxed flex-1 overflow-y-auto whitespace-pre-wrap" data-testid="message-body">
              {/* 04/06/2026 — `as="div"` instead of `as="span"`. A `<span>` with
                  inline-level `<br/>` children was occasionally being collapsed
                  by the browser during the monthly tick re-render, causing the
                  reported "tout part en vrille pendant un jour" visual glitch.
                  Defensive `whitespace-pre-wrap` on the parent guarantees that
                  raw `\n` are still rendered as line breaks even if MarkdownLite
                  ever fails to parse them. */}
              <MarkdownLite
                text={selected.body}
                as="div"
                strongClassName="text-foreground font-semibold"
              />
            </div>

            {selected.kind === 'decision' && !selected.replied && (selected.choices || []).length > 0 && (
              <div className="px-4 py-3 border-t border-ink-600 bg-ink-700/30 space-y-2" data-testid="message-choices">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Votre décision</p>
                {selected.choices.map(c => {
                  const effectsText = (c.effects || []).map(e => effectLabel(e)).filter(Boolean).join(', ');
                  return (
                    <button
                      key={c.id}
                      onClick={() => handleChoice(selected.id, c.id)}
                      className="w-full text-left px-3 py-2 rounded border border-ink-600 bg-ink-700/30 hover:border-accent hover:bg-accent/5 transition-colors group"
                      data-testid={`choice-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-slate-200 group-hover:text-accent">{c.label}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-accent" />
                      </div>
                      {effectsText && <p className="text-[10px] text-slate-500 mt-0.5">{effectsText}</p>}
                    </button>
                  );
                })}
                <p className="text-[10px] text-slate-500 italic">
                  ⏱ Sans réponse avant J{selected.expiresDay}, le choix par défaut « {selected.choices.find(c => c.id === selected.defaultChoiceId)?.label || 'aucune action'} » sera appliqué.
                </p>
              </div>
            )}

            {selected.replied && (
              <div className="px-4 py-2 border-t border-ink-600 bg-emerald-500/10 dark:bg-emerald-500/5 text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Décision appliquée : {selected.choices.find(c => c.id === selected.resolvedChoiceId)?.label || selected.resolvedChoiceId}
              </div>
            )}

            {/* 28/05/2026 v2 — opportunistic office upgrade proposal.
                The message comes from a supplier with a structured
                payload — we render a single Accept CTA + cost recap.
                Once accepted, the message stays in the inbox but the
                CTA disappears (and we mark the message as replied via
                the standard `markReplied` helper). */}
            {selected.payload && selected.payload.kind === 'office_proposal' && !selected.replied && (
              <OfficeProposalCTA
                upgradeId={selected.payload.upgradeId}
                messageId={selected.id}
                onAccepted={() => setSelectedId(null)}
              />
            )}
          </article>
        )}
      </div>
    </div>
  );
};

// Inline icon-row used in the sidebar
const SidebarBtn = ({ label, icon: Icon, active, onClick, count, testId, highlight, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors ${active ? 'bg-accent/15 text-accent border border-accent/30' : 'text-slate-300 hover:bg-ink-700/50 border border-transparent'}`}
    data-testid={testId}
  >
    <span className="flex items-center gap-2 min-w-0">
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </span>
    {count > 0 && (
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${danger ? 'bg-rose-500/25 text-rose-200' : highlight ? 'bg-accent/30 text-accent' : 'bg-ink-700/40 text-slate-400'}`}>
        {count}
      </span>
    )}
  </button>
);

// Build a short human-readable summary for a single effect (used as choice
// preview text). Unknown kinds are silently omitted.
const effectLabel = (eff) => {
  switch (eff.kind) {
    case 'treasury':           return `${eff.amount >= 0 ? '+' : ''}${formatMoney(eff.amount)} trésorerie`;
    case 'reputation':         return `${eff.amount >= 0 ? '+' : ''}${eff.amount} réputation`;
    case 'notoriety':          return `${eff.amount >= 0 ? '+' : ''}${eff.amount} notoriété`;
    case 'driverRaise':        return `salaire ${eff.amount >= 0 ? '+' : ''}${formatMoney(eff.amount)} / mois`;
    case 'driverSatisfaction': return `satisfaction ${eff.amount >= 0 ? '+' : ''}${eff.amount}`;
    case 'driverFire':         return 'chauffeur quitte';
    case 'creditScore':        return `score crédit ${eff.amount >= 0 ? '+' : ''}${eff.amount}`;
    case 'creditOffer':        return `crédit ${formatMoney(eff.amount)} (${eff.months} mois, ${Math.round((eff.rate || 0) * 100)} %)`;
    // 17/06/2026 (Lot 6 Phase 3) — Effets diplomatie.
    case 'competitorRelation': return `relation ${eff.amount >= 0 ? '+' : ''}${eff.amount}`;
    case 'flag':               return null; // pas visible côté joueur
    default: return null;
  }
};

// 28/05/2026 v2 — CTA for opportunistic office upgrade proposals coming
// from suppliers. The accept button pays the install cost via the
// office slice and marks the message as replied so it stops showing.
const OfficeProposalCTA = ({ upgradeId, messageId, onAccepted }) => {
  const upgrade = OFFICE_UPGRADE_BY_ID[upgradeId];
  const treasury = useGameStore(s => s.treasury);
  const owned = useGameStore(s => (s.officeUpgrades || {})[upgradeId]);
  const acceptOfficeUpgrade = useGameStore(s => s.acceptOfficeUpgrade);
  const markReplied = useGameStore(s => s.markMessageReplied);
  const [err, setErr] = useState('');

  if (!upgrade) return null;

  const accept = () => {
    const r = acceptOfficeUpgrade(upgradeId);
    if (!r || r.ok === false) {
      setErr(r?.reason === 'insufficient_funds'
        ? `Trésorerie insuffisante (${formatMoney(upgrade.costBuy)} requis)`
        : `Erreur : ${r?.reason || 'inconnue'}`);
      return;
    }
    try { if (typeof markReplied === 'function') markReplied(messageId, 'accepted'); } catch (e) { /* noop */ }
    if (typeof onAccepted === 'function') onAccepted();
  };

  return (
    <div className="px-4 py-3 border-t border-ink-600 bg-emerald-500/5 space-y-2" data-testid={`office-cta-${upgradeId}`}>
      <p className="text-[10px] uppercase tracking-wider text-money font-mono flex items-center gap-1.5">
        <Heart className="w-3 h-3" /> Proposition fournisseur
      </p>
      <div className="text-xs text-slate-300 space-y-0.5">
        <p>Installation : <strong className="text-foreground">{formatMoney(upgrade.costBuy)}</strong></p>
        <p>Abonnement : <strong className="text-danger">{formatMoney(upgrade.chargeMonthly)} / mois</strong></p>
        <p>Effet : <strong className="text-money">+{upgrade.satisfactionBoost} satisfaction</strong> permanent (toute l&apos;équipe)</p>
      </div>
      {owned ? (
        <p className="text-[11px] text-money italic">✓ Déjà installé. Vous pouvez archiver ce message.</p>
      ) : (
        <>
          <button
            onClick={accept}
            disabled={treasury < upgrade.costBuy}
            className="w-full bg-money text-white text-xs font-bold px-3 py-2 rounded hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid={`office-cta-accept-${upgradeId}`}
          >
            Accepter — installer {upgrade.icon} {upgrade.name}
          </button>
          {err && <p className="text-[10px] text-danger">{err}</p>}
        </>
      )}
    </div>
  );
};

export default Messagerie;
